import { useRef } from "react"
import Head from "next/head"
import Image from "components/Image"
import Link from "next/link"
import tw, { css, styled } from "twin.macro"
import Slider from "react-slick"

import { SlidesProps } from "components/dynamic"
import { breakpoint } from "styles/Variables"
import { BodyDetail, H2, IntroBody } from "styles/Text"

import ArrowLeft from "icons/ArrowLeft"
import ArrowRight from "icons/ArrowRight"

import TransparentOverlay from "components/Heroes/TransparentOverlay"
import PrimaryButton from "components/Buttons/Primary"
import Language from "components/Language"

const SlideBody = styled.div(() => [
  tw`
    h-full flex flex-col justify-between items-center text-center text-white py-20 px-8
    relative
    z-10
    opacity-0 
    lg:(px-11)
  `,
])

const SlidesStyled = styled.div`
  z-index: 1;
  position: relative;
  width: 100%;
  margin: 0 auto;

  .slick-list {
    height: 585px;
    width: 100%;

    ${breakpoint.lg`
      height: 700px;
    `}
  }
  .slick-slide {
    width: 335px;
    opacity: 0.3;
    transition: opacity 0.3s;
    &.slick-active {
      opacity: 1;
      ${SlideBody} {
        opacity: 1;
      }
    }
    ${breakpoint.lg`

      width: 400px;
    `}
  }
`
const Slide = styled.div(() => [
  tw`relative outline-none`,
  css`
    height: 585px;
    width: 100%;

    ${breakpoint.lg`
      height: 700px;

    `}
  `,
])

const SliderButton = tw.button`
  cursor-pointer
  w-12.5 h-12.5
  bg-white rounded-full shadow-2xl

  md:(w-20 h-20)
`

const Slides = ({ location_pages, ShowButtons = false }: SlidesProps) => {
  const slider = useRef(null)
  const settings = {
    infinite: true,
    slidesToShow: 2,
    slidesToScroll: 1,
    centerMode: true,
    draggable: true,
    arrows: false,
    variableWidth: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  }
  return (
    <>
      <Head>
        <link href="/assets/slick.css" rel="stylesheet" key="slick-css" />
      </Head>

      <div tw="bg-deep-blue mb-15 lg:mb-20">
        <div css={{ width: "2000px", maxWidth: "100%" }} tw="mx-auto relative">
          <SlidesStyled>
            <Slider ref={slider} {...settings}>
              {location_pages.map(
                ({ id, Hero, UID, Name, Description, LFLNameID, region }) => (
                  <Slide>
                    {Hero?.BackgroundImage?.url && (
                      <Image
                        css={css`
                          z-index: -1;
                        `}
                        layout="fill"
                        sizes="(min-width: 1200px) 400px, 335px"
                        key={id}
                        src={Hero?.BackgroundImage.url}
                        alt={Hero?.BackgroundImage.alternativeText}
                        objectFit="cover"
                        objectPosition="center"
                      />
                    )}

                    <TransparentOverlay
                      css={css`
                        z-index: -1;
                      `}
                    />

                    <SlideBody>
                      <div />
                      <div>
                        {(region && (
                          <H2 as="h3">
                            <Language unwrap>
                              {region?.LFLNameID
                                ? `[${region.Name}](lflp/words/${region.LFLNameID})`
                                : region.Name}
                            </Language>
                          </H2>
                        )) || <div tw="h-11" />}

                        {Name && (
                          <IntroBody as="h3" tw="font-normal mt-4">
                            <Language unwrap>
                              {LFLNameID
                                ? `[${Name}](/lflp/words/${LFLNameID})`
                                : Name}
                            </Language>
                          </IntroBody>
                        )}
                      </div>
                      <div>
                        {(Description && (
                          <BodyDetail tw="mt-4">
                            <Language unwrap>{Description}</Language>
                          </BodyDetail>
                        )) || <div tw="pb-25 pt-1" />}

                        {ShowButtons && (
                          <Link href={`/location/${UID}`} passHref>
                            <PrimaryButton tw="mt-7.5">
                              Learn more
                            </PrimaryButton>
                          </Link>
                        )}
                      </div>
                    </SlideBody>
                  </Slide>
                )
              )}
            </Slider>
          </SlidesStyled>
          <SliderButton
            tw="absolute top-1/2 left-4 transform text-primary z-10 2xl:(-translate-y-1/2 left-72)"
            type="button"
            aria-label="previous"
            onClick={() => slider.current.slickPrev()}
          >
            <ArrowLeft />
          </SliderButton>

          <SliderButton
            tw="absolute top-1/2 right-4 transform text-primary z-10 2xl:(-translate-y-1/2 right-72)"
            type="button"
            aria-label="next"
            onClick={() => slider.current.slickNext()}
          >
            <ArrowRight />
          </SliderButton>
        </div>
      </div>
    </>
  )
}

export default Slides
